<script setup lang="ts">
import { ref, inject, computed, watch, type Ref } from 'vue'
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import {
	UserCog, Users, HardDrive, Clock, CloudUpload,
	Tags, Captions, Activity, List, MonitorPlay, Cpu, Headset
} from 'lucide-vue-next'
import billing from '@/services/Billing'
import type { BillingInfo } from '@/types/Billing'

const props = defineProps<{
	modelValue: boolean
}>()

const emit = defineEmits<{
	(e: 'update:modelValue', value: boolean): void
	(e: 'contact'): void
}>()

const isOpen = computed({
	get: () => props.modelValue,
	set: (val: boolean) => emit('update:modelValue', val)
})

const vhostId = inject<Ref<string>>('vhostId', ref(''))
const vhostInfo = inject<Ref<any> | null>('vhostInfo', null)

const { getBillingInfo } = billing()
const billingInfo = ref<BillingInfo | null>(null)
const loading = ref(false)

// Current plan name normalised to lowercase
const currentPlan = computed(() => {
	if (billingInfo.value?.billingPlan) return billingInfo.value.billingPlan.toLowerCase()
	if (vhostInfo?.value?.billingPlan) return vhostInfo.value.billingPlan.toLowerCase()
	return 'free'
})

// ── Plan definitions matching v6 system.html exactly ──

interface PlanFeature {
	icon: any
	label: string
}

interface PlanDef {
	id: string
	name: string
	price: string
	period: string
	features: PlanFeature[]
	cta: string
	ctaVariant: 'default' | 'outline' | 'secondary'
	contact?: boolean
}

const planDefs: PlanDef[] = [
	{
		id: 'free',
		name: 'Free',
		price: 'Free',
		period: '',
		features: [
			{ icon: UserCog, label: '1 Admin user' },
			{ icon: Users, label: '10 Content contributors' },
			{ icon: HardDrive, label: '50GB of storage' },
			{ icon: Clock, label: '10 Minutes of AI processing' },
			{ icon: CloudUpload, label: '3 Published files' },
		],
		cta: 'Current Plan',
		ctaVariant: 'secondary',
	},
	{
		id: 'paid',
		name: 'Paid',
		price: '$39.99',
		period: '/Month',
		features: [
			{ icon: UserCog, label: '1 Admin user' },
			{ icon: Users, label: '10 Content contributors' },
			{ icon: HardDrive, label: '1TB of storage' },
			{ icon: Clock, label: '60 Minutes of AI processing' },
			{ icon: CloudUpload, label: '30 Published files' },
		],
		cta: 'Upgrade Now',
		ctaVariant: 'default',
	},
	{
		id: 'enterprise',
		name: 'Enterprise',
		price: 'Starts at $1,500',
		period: '/Month',
		features: [
			{ icon: UserCog, label: 'Unlimited users' },
			{ icon: CloudUpload, label: 'Unlimited publishing destinations' },
			{ icon: Tags, label: 'Custom AI tagging calls' },
			{ icon: Captions, label: 'Custom caption minutes' },
			{ icon: HardDrive, label: 'Custom and/or local storage' },
			{ icon: Activity, label: 'Custom bandwidth' },
			{ icon: List, label: 'Custom meta-data' },
			{ icon: MonitorPlay, label: 'Custom media transformations' },
			{ icon: Cpu, label: 'API access' },
			{ icon: Headset, label: 'Customer success manager' },
		],
		cta: 'Contact Us',
		ctaVariant: 'outline',
		contact: true,
	},
]

const isCurrent = (plan: PlanDef) => plan.id === currentPlan.value

const handleCta = (plan: PlanDef) => {
	if (plan.contact) {
		emit('contact')
		window.open('mailto:sales@newspark.io?subject=Enterprise%20Plan%20Inquiry', '_blank')
	} else if (!isCurrent(plan)) {
		window.location.href = '/v7/settings'
	}
	isOpen.value = false
}

const fetchData = async () => {
	loading.value = true
	try {
		if (vhostId.value) {
			billingInfo.value = await getBillingInfo(vhostId.value)
		}
	} catch (err) {
		console.error('Error loading billing data:', err)
	} finally {
		loading.value = false
	}
}

// Fetch billing info when sheet opens to determine current plan
watch(isOpen, (val) => {
	if (val && !billingInfo.value) fetchData()
})
</script>

<template>
	<Sheet v-model:open="isOpen">
		<SheetContent side="top" class="max-h-[85vh] overflow-y-auto p-6 md:p-10">
			<SheetHeader class="pb-6">
				<SheetTitle class="text-xl">Choose Your Plan</SheetTitle>
				<SheetDescription>Select the plan that best fits your needs. Upgrade or downgrade at any time.</SheetDescription>
			</SheetHeader>

			<!-- Plans grid -->
			<div class="grid grid-cols-1 md:grid-cols-3 gap-6 pb-6 px-2 md:px-8">
				<Card
					v-for="plan in planDefs"
					:key="plan.id"
					class="relative overflow-hidden transition-all duration-200"
					:class="isCurrent(plan) ? 'ring-2 ring-primary shadow-lg' : 'hover:shadow-md'"
				>
					<!-- Plan header band -->
					<div
						class="py-3 px-4 text-white text-center"
						:class="isCurrent(plan) ? 'bg-primary' : 'bg-muted-foreground/70'"
					>
						<div v-if="isCurrent(plan)" class="text-xs font-medium uppercase tracking-wide mb-1 opacity-80">Current Plan</div>
						<div class="text-lg font-bold">{{ plan.name }}</div>
					</div>

					<CardContent class="p-5 flex flex-col" style="min-height: 380px">
						<!-- Pricing -->
						<div class="text-center py-3 border-b mb-5">
							<span class="text-2xl font-bold text-foreground">{{ plan.price }}</span>
							<span v-if="plan.period" class="text-sm text-muted-foreground">{{ plan.period }}</span>
						</div>

						<!-- Features list -->
						<ul class="space-y-3 mb-6 flex-1">
							<li
								v-for="(feature, idx) in plan.features"
								:key="idx"
								class="flex items-center gap-3 text-sm"
							>
								<component :is="feature.icon" class="h-4 w-4 shrink-0 text-primary" />
								<span class="text-foreground">{{ feature.label }}</span>
							</li>
						</ul>

						<!-- CTA Button -->
						<Button
							class="w-full"
							:variant="isCurrent(plan) ? 'secondary' : plan.ctaVariant"
							:disabled="isCurrent(plan)"
							@click="handleCta(plan)"
						>
							{{ isCurrent(plan) ? 'Current Plan' : plan.cta }}
						</Button>
					</CardContent>
				</Card>
			</div>
		</SheetContent>
	</Sheet>
</template>
